import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Drawer from '../Drawer';
import { fetchMedicalCenters } from '~/services/medical-centers';
import { debounce } from 'lodash';

interface Center {
    id: number;
    name: string;
    location: string;
}

interface Props {
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
    onAddNew?: (typedName: string) => void;
}

// Wraps every case-insensitive occurrence of `term` inside `text` with a
// <strong> so the user can see why a row matched their query.
const highlightMatch = (text: string, term: string) => {
    if (!term) return text;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'ig'));
    return parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase() ? (
            <strong key={i}>{part}</strong>
        ) : (
            <span key={i}>{part}</span>
        )
    );
};

const AddMedicalCenterDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    onAddNew,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [data, setData] = useState<Center[]>([]);
    const [loading, setLoading] = useState(false);

    const getMedicalCenters = async (term: string) => {
        setLoading(true);
        const res = await fetchMedicalCenters({ name: term });
        setData(res.results);
        setLoading(false);
    };

    useEffect(() => {
        getMedicalCenters('');
    }, []);

    const debouncedFetch = useCallback(
        debounce((term: string) => {
            getMedicalCenters(term);
        }, 500),
        []
    );

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        debouncedFetch(newSearchTerm);
    };

    const resetAndCloseForm = () => {
        setIsOpen(false);
        setSearchTerm('');
        setData([]);
    };

    const handleAddNew = () => {
        const typed = searchTerm;
        // Close this drawer and signal the parent to open the create form,
        // pre-populated with whatever the user has typed.
        resetAndCloseForm();
        onAddNew?.(typed);
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Add Medical Center'>
            <label className='text-[12px]/[14px] font-extrabold'>
                Name{' '}
                <span className='mb-[3px] text-[10px]/[10px] text-[#F02D3A]'>
                    *
                </span>
            </label>
            <div className='mb-[5px] flex rounded-lg border border-[#949599] p-[10px] focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'>
                <input
                    className='w-full focus:outline-none'
                    type='text'
                    value={searchTerm}
                    placeholder='Start typing name'
                    onChange={handleSearchTermChange}
                />
            </div>
            <p className='text-[12px]/[16px]'>
                This is to ensure duplicate Centers do not get added.
            </p>
            {loading ? (
                <div className='mt-4 text-center'>
                    <span>Loading...</span>
                </div>
            ) : (
                <table className='mt-[25px] w-full'>
                    <thead>
                        <tr className='bg-[#F4F4F5]'>
                            <th className='w-2/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                Name
                            </th>
                            <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                <span className='text-[14px]/[16px]'>
                                    State
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={2}
                                    className='p-[16px] text-center text-[14px]/[16px] text-[#949599]'
                                >
                                    No matching centers in the system.
                                </td>
                            </tr>
                        ) : (
                            data.map((center) => (
                                <tr key={center.id}>
                                    <td className='p-[10px]'>
                                        <Link
                                            className='font-bold text-primary hover:underline'
                                            to={`/medical-centers/${center.id}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {highlightMatch(
                                                center?.name ?? '',
                                                searchTerm
                                            )}
                                        </Link>
                                    </td>
                                    <td className='p-[10px]'>
                                        {center?.location}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}
            <p className='mt-[16px] text-[12px]/[16px] text-[#131212]'>
                Don't see the medical center you're looking for?
            </p>
            <div className='mt-[30px] flex'>
                <button
                    type='button'
                    className='m-auto mr-3 justify-self-end rounded-3xl border-[2px] border-black bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase hover:bg-black hover:text-white'
                    onClick={resetAndCloseForm}
                >
                    Cancel
                </button>
                <button
                    type='button'
                    onClick={handleAddNew}
                    className='m-auto mr-3 rounded-full bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#131212] hover:bg-opacity-90'
                >
                    + Add New Medical Center
                </button>
            </div>
        </Drawer>
    );
};

export default AddMedicalCenterDrawer;
